/**
 * ReviewActionDialog (Story 24.4 — Epic 24: Report Approval Workflow)
 *
 * One centered modal (UI directive §4.4) for the three pending-review verdicts:
 *   - approve         → POST /:id/approve
 *   - request_changes → POST /:id/request-changes  { comment }  (comment required)
 *   - withdraw        → POST /:id/withdraw
 *
 * Self-contained: owns its mutation + comment state. Self-review is blocked
 * server-side; the 400 field message surfaces inline. Kept mounted + gated by
 * `open` (§4.6). The full feedback history lives in the 24-6 audit trail.
 */

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@provenleap/ui";
import { CheckCircle, ChatText, ArrowUUpLeft } from "@phosphor-icons/react";
import { apiPost } from "../../lib/apiClient";
import type { ReportListItem } from "./ReportTableView";

export type ReviewAction = "approve" | "request_changes" | "withdraw";

const COPY: Record<
  ReviewAction,
  { title: string; endpoint: string; verb: string; busy: string; variant: "brand" | "destructive" }
> = {
  approve: { title: "Approve this report?", endpoint: "approve", verb: "Approve", busy: "Approving…", variant: "brand" },
  request_changes: { title: "Request changes", endpoint: "request-changes", verb: "Request changes", busy: "Sending…", variant: "brand" },
  withdraw: { title: "Withdraw submission?", endpoint: "withdraw", verb: "Withdraw", busy: "Withdrawing…", variant: "destructive" },
};

interface ReviewActionDialogProps {
  report: ReportListItem | null;
  action: ReviewAction;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onActioned: (action: ReviewAction) => void;
}

export function ReviewActionDialog({
  report,
  action,
  open,
  onOpenChange,
  onActioned,
}: ReviewActionDialogProps) {
  const queryClient = useQueryClient();
  const [comment, setComment] = useState("");
  const copy = COPY[action];

  const mutation = useMutation({
    mutationFn: async () => {
      if (!report) return;
      const body =
        action === "request_changes" ? { comment: comment.trim() } : undefined;
      const res = await apiPost(`/api/reports/${report.id}/${copy.endpoint}`, body);
      if (!res.ok) {
        const err = await res.json().catch(() => ({ detail: "Action failed" }));
        throw new Error(
          err.errors?.[0]?.message ?? err.detail ?? err.title ?? "Action failed",
        );
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
      onActioned(action);
      onOpenChange(false);
    },
  });

  const resetMutation = mutation.reset;
  useEffect(() => {
    if (open) {
      setComment("");
      resetMutation();
    }
  }, [open, resetMutation]);

  function handleOpenChange(next: boolean) {
    if (!next && mutation.isPending) return;
    onOpenChange(next);
  }

  const commentRequired = action === "request_changes";
  const canSubmit = !commentRequired || comment.trim().length > 0;
  const error = mutation.error instanceof Error ? mutation.error.message : null;
  const Icon =
    action === "approve" ? CheckCircle : action === "withdraw" ? ArrowUUpLeft : ChatText;

  const description =
    action === "approve"
      ? report
        ? `"${report.name}" will be marked Approved and the author notified.`
        : "This report will be marked Approved."
      : action === "withdraw"
        ? report
          ? `"${report.name}" will be pulled out of review (back to the author).`
          : "This report will be pulled out of review."
        : report
          ? `Tell the author what to fix on "${report.name}". They'll be notified.`
          : "Tell the author what to fix.";

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleOpenChange}>
      <AnimatePresence>
        {open && (
          <DialogPrimitive.Portal forceMount>
            <DialogPrimitive.Overlay asChild forceMount>
              <motion.div
                className="fixed inset-0 z-[60] bg-black/35"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
              />
            </DialogPrimitive.Overlay>
            <DialogPrimitive.Content asChild forceMount>
              <motion.div
                className="fixed left-1/2 top-1/2 z-[60] w-full max-w-md -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                initial={{ opacity: 0, scale: 0.95, y: 10 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 10 }}
                transition={{ duration: 0.25, ease: "easeOut" }}
                data-testid="review-action-dialog"
              >
                <div className="flex flex-col space-y-3 text-left">
                  <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                    {copy.title}
                  </DialogPrimitive.Title>
                  <DialogPrimitive.Description className="text-sm text-muted-foreground">
                    {description}
                  </DialogPrimitive.Description>
                </div>

                {commentRequired && (
                  <textarea
                    data-testid="review-comment"
                    aria-label="Feedback for the author"
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    disabled={mutation.isPending}
                    rows={4}
                    maxLength={2000}
                    placeholder="What needs to change?"
                    className="mt-5 w-full rounded-2xl bg-background px-3 py-2 text-[13px] text-foreground shadow-xs outline-none resize-none"
                    style={{ border: "0.5px solid var(--hairline)" }}
                  />
                )}

                {error && (
                  <p className="mt-4 text-[13px] text-destructive" role="alert">
                    {error}
                  </p>
                )}

                <div className="flex justify-end gap-3 mt-6">
                  <Button
                    variant="outline"
                    className="rounded-2xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                    style={{ border: "0.5px solid var(--hairline)" }}
                    onClick={() => handleOpenChange(false)}
                    disabled={mutation.isPending}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant={copy.variant}
                    className="rounded-2xl text-[13px] cursor-pointer"
                    onClick={() => mutation.mutate()}
                    disabled={mutation.isPending || !canSubmit}
                    data-testid="confirm-review-action-btn"
                  >
                    <Icon size={16} className="mr-1" />
                    {mutation.isPending ? copy.busy : copy.verb}
                  </Button>
                </div>
              </motion.div>
            </DialogPrimitive.Content>
          </DialogPrimitive.Portal>
        )}
      </AnimatePresence>
    </DialogPrimitive.Root>
  );
}
